import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Container,
  Paper,
  CircularProgress,
  Chip,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert
  , FormControlLabel, Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { useLanguage } from '../context/LanguageContext';
import Breadcrumb from '../components/Breadcrumb';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

// Feedback Audio Player Component
function FeedbackAudioPlayer({ feedbackPath, label, color }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handlePlayClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/result/file/${encodeURIComponent(feedbackPath)}`);
      const blob = await res.blob();
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      audio.play();
    } catch (err) {
      console.error('Error loading audio:', err);
      setError('Failed to load audio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: color, display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
        <IconButton
          size="small"
          onClick={handlePlayClick}
          disabled={loading}
          sx={{ color: color }}
        >
          {loading ? <CircularProgress size={24} /> : <PlayCircleIcon />}
        </IconButton>
      </Box>
      {error && (
        <Typography variant="caption" sx={{ color: '#f44336', ml: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

// Result display component (shared across all views)
function ResultDisplay({ grandhamToShow, marks, ratings, lang, labels, parikshaadhikariFeedback }) {
  const getPassPercentage = () => {
    const currentStartIndex = grandhamToShow.previous.length;
    return marks[currentStartIndex] || '0%';
  };

  const getPreviousgrandhamStatus = () => {
    if (!grandhamToShow.previous || grandhamToShow.previous.length === 0) {
      return null;
    }

    let passCount = 0;
    let failCount = 0;
    let totalCount = 0;

    grandhamToShow.previous.forEach((item, index) => {
      const key = `prev_${item.section}_${index}`;
      const sectionRating = ratings[key] || item.rating || {};

      // Check if this is non-Krishna Yajurveda (has _items)
      const isNonKrishna = item.isNonKrishna || item.data?._items;

      if (isNonKrishna) {
        // For non-Krishna: ratings are flat
        (item.data?._items || []).forEach((itemName) => {
          const rating = sectionRating[itemName];
          totalCount++;
          if (rating === 'pass') {
            passCount++;
          } else if (rating === 'fail') {
            failCount++;
          }
        });
      } else {
        // For Krishna: ratings are nested
        Object.keys(item.data || {}).forEach((kanda) => {
          const pannams = item.data[kanda];
          if (Array.isArray(pannams)) {
            pannams.forEach((pannam) => {
              const rating = sectionRating[kanda]?.[pannam];
              totalCount++;
              if (rating === 'pass') {
                passCount++;
              } else if (rating === 'fail') {
                failCount++;
              }
            });
          }
        });
      }
    });

    if (totalCount === 0) return null;

    // All pass
    if (passCount === totalCount && failCount === 0) {
      return {
        text: labels.fullyGiven,
        color: '#4caf50' // Green
      };
    }

    // All fail
    if (failCount === totalCount && passCount === 0) {
      return {
        text: labels.nothingGiven,
        color: '#f44336' // Red
      };
    }

    // Mixed
    return {
      text: labels.partiallyGiven,
      color: '#ff9800' // Amber
    };
  };

  const renderGrandhamSection = (item, index, isPrevious) => {
    const key = `${isPrevious ? 'prev' : 'curr'}_${item.section}_${index}`;
    const sectionRating = ratings[key] || item.rating || {};
    const sectionCompleted = item.completed || {};

    // Check if this is a non-Krishna Yajurveda item (has _items)
    const isNonKrishna = item.isNonKrishna || item.data?._items;

    return (
      <Box key={key} sx={{ mb: 3, p: 2, backgroundColor: isPrevious ? '#fff9e6' : '#f0f7ff', borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#7f4b2d', mb: 2 }}>
          {item.section}
        </Typography>
        
        {isNonKrishna ? (
          // For non-Krishna Yajurveda: Handle flat structure with _items
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(item.data?._items || []).map((itemName) => {
              const rating = sectionRating[itemName];
              const isCompleted = sectionCompleted[itemName];
              
              // For previous grandham, show completion status
              if (isPrevious) {
                return (
                  <Chip
                    key={itemName}
                    label={itemName}
                    size="small"
                    color={isCompleted || rating === 'pass' ? 'success' : rating === 'fail' ? 'error' : 'default'}
                    variant={isCompleted || rating === 'pass' || rating === 'fail' ? 'filled' : 'outlined'}
                  />
                );
              }
              
              // For current grandham, show rating status
              return (
                <Chip
                  key={itemName}
                  label={`${itemName}: ${rating || '-'}`}
                  size="small"
                  color={rating === 'pass' ? 'success' : rating === 'fail' ? 'error' : 'default'}
                  variant={rating ? 'filled' : 'outlined'}
                />
              );
            })}
          </Box>
        ) : (
          // For Krishna Yajurveda: Handle nested structure
          Object.keys(item.data || {}).map((kanda) => {
            const pannams = item.data[kanda];
            if (!Array.isArray(pannams)) return null;

            return (
              <Box key={kanda} sx={{ mb: 2, pl: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#444', mb: 1 }}>
                  {kanda}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {pannams.map((pannam) => {
                    const rating = sectionRating[kanda]?.[pannam];
                    const isCompleted = sectionCompleted[kanda]?.[pannam];
                    
                    // For previous grandham, show completion status
                    if (isPrevious) {
                      return (
                        <Chip
                          key={pannam}
                          label={pannam}
                          size="small"
                          color={isCompleted || rating === 'pass' ? 'success' : rating === 'fail' ? 'error' : 'default'}
                          variant={isCompleted || rating === 'pass' || rating === 'fail' ? 'filled' : 'outlined'}
                        />
                      );
                    }
                    
                    // For current grandham, show numeric rating
                    return (
                      <Chip
                        key={pannam}
                        label={`${pannam}: ${rating || '-'}`}
                        size="small"
                        color={rating >= 7 ? 'success' : rating >= 5 ? 'warning' : 'default'}
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })
        )}
        
        {!isPrevious && item.marks && (
          <Typography variant="body2" sx={{ mt: 2, fontWeight: 600, color: '#1976d2' }}>
            {labels.passPercentage}: {item.marks}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Previous Grandham */}
      {grandhamToShow.previous && grandhamToShow.previous.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#ff9800', mb: 1 }}>
            {labels.previousGrandham}
          </Typography>
          {getPreviousgrandhamStatus() && (
            <Typography variant="body2" sx={{ color: getPreviousgrandhamStatus().color, mb: 2, fontStyle: 'italic', fontWeight: 500 }}>
              {getPreviousgrandhamStatus().text}
            </Typography>
          )}
          {grandhamToShow.previous.map((item, index) => renderGrandhamSection(item, index, true))}
        </Box>
      )}

      {/* Current Grandham */}
      {grandhamToShow.current && grandhamToShow.current.length > 0 && (
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#4caf50', mb: 2 }}>
            {labels.currentGrandham}
          </Typography>
          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            {labels.passPercentage}: {getPassPercentage()}
          </Typography>
          {grandhamToShow.current.map((item, index) => renderGrandhamSection(item, index, false))}
        </Box>
      )}

      {(!grandhamToShow.previous || grandhamToShow.previous.length === 0) &&
       (!grandhamToShow.current || grandhamToShow.current.length === 0) && (
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
          {labels.noData}
        </Typography>
      )}

      {/* Parikshaadhikari Feedback Section */}
      {parikshaadhikariFeedback && Array.isArray(parikshaadhikariFeedback) && parikshaadhikariFeedback.length > 0 && (
        <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#7f4b2d', mb: 2 }}>
            {labels.parikshaadhikariFeedback}
          </Typography>
          {parikshaadhikariFeedback
            .filter(feedback => feedback.positive_feedback_path || feedback.improvement_feedback_path)
            .map((feedback, idx) => {
              const parikshaadhikariName = lang === 'telugu' ? feedback.parikshaadhikari_telugu : feedback.parikshaadhikari_sanskrit;
              return (
                <Box key={idx} sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#555', mb: 2 }}>
                    👤 {parikshaadhikariName || feedback.parikshaadhikari_id}
                  </Typography>
                  {feedback.positive_feedback_path && (
                    <FeedbackAudioPlayer
                      feedbackPath={feedback.positive_feedback_path}
                      label={`✓ ${labels.positiveFeedback}`}
                      color="#4caf50"
                    />
                  )}
                  {feedback.improvement_feedback_path && (
                    <FeedbackAudioPlayer
                      feedbackPath={feedback.improvement_feedback_path}
                      label={`⚠ ${labels.improvementFeedback}`}
                      color="#ff9800"
                    />
                  )}
                </Box>
              );
            })}
        </Box>
      )}
    </Box>
  );
}

export default function ParikshaResult() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [yearList, setYearList] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  
  // For PathashalaAdmin role
  const [pathasalaList, setPathasalaList] = useState([]);
  const [selectedPathasala, setSelectedPathasala] = useState('');
  const [vatuList, setVatuList] = useState([]);
  const [selectedVatu, setSelectedVatu] = useState('');
  
  // For Admin/SuperUser role
  const [allResults, setAllResults] = useState([]);
  const [parikshaTypes, setParikshaTypes] = useState([]);
  const [selectedParikshaType, setSelectedParikshaType] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  
  // For general users
  const [aadhar, setAadhar] = useState('');
  const [aadharError, setAadharError] = useState('');
  const [searchError, setSearchError] = useState('');

  
  // Common result data
  const [grandhamToShow, setGrandhamToShow] = useState({ previous: [], current: [] });
  const [marks, setMarks] = useState({});
  const [ratings, setRatings] = useState({});

  const labels = {
    telugu: {
      title: 'పరీక్ష ఫలితములు',
      teluguYear: 'తెలుగు సంవత్సరం',
      pathasala: 'పాఠశాల',
      vatu: 'వటువు',
      aadhar: 'ఆధార్ నంబర్',
      selectYear: 'సంవత్సరం ఎంచుకోండి',
      selectPathasala: 'పాఠశాల ఎంచుకోండి',
      selectVatu: 'వటువును ఎంచుకోండి',
      search: 'వెతకండి',
      loading: 'లోడవుతోంది...',
      noData: 'డేటా లేదు',
      previousGrandham: 'పూర్వ గ్రంథము',
      currentGrandham: 'ప్రస్తుత గ్రంథము',
      passPercentage: 'ఉత్తీర్ణత',
      vatuName: 'వటువు పేరు',
      pathasalaName: 'పాఠశాల పేరు',
      reward: 'పురస్కారం',
      examReward: 'పరీక్ష పురస్కారం',
      expenseAmount: 'చార్జీలు',
      rewardFormula: 'మొత్తం',
      close: 'మూసివేయి',
      enterAadhar: 'ఆధార్ నంబర్ నమోదు చేయండి',
      aadharRequired: 'ఆధార్ నంబర్ తప్పనిసరి',
      aadharInvalid: 'ఆధార్ తప్పనిసరిగా 12 అంకెలు ఉండాలి',
      results: 'ఫలితాలు',
      fullyGiven: 'సంపూర్ణంగా ఇవ్వబడినవి',
      partiallyGiven: 'పాక్షికంగా ఇవ్వబడినవి',
      nothingGiven: 'ఏమీ ఇవ్వబడలేదు',
      parikshaadhikariFeedback: 'పరీక్ష అధికారి వ్యాఖ్యలు',
      positiveFeedback: 'సానుకూల వ్యాఖ్యలు',
      improvementFeedback: 'సుధారణ వ్యాఖ్యలు',
      parikshaType: 'పరీక్ష రకం',
      selectParikshaType: 'పరీక్ష రకం ఎంచుకోండి',
      krishnaYajurveda: 'కృష్ణ యజుర్వేదం',
      sanskrit: 'సంస్కృతం',
      vedadhyayiSahityam: 'వేదాధ్యయి సాహిత్యం',
      lakshanam: 'లక్షణం'
    },
    sanskrit: {
      title: 'परीक्षाफलानि',
      teluguYear: 'तेलुगु संवत्सरः',
      pathasala: 'पाठशाला',
      vatu: 'वाटुवु',
      aadhar: 'आधार् सङ्ख्या',
      selectYear: 'संवत्सरं चिनोतु',
      selectPathasala: 'पाठशालां चिनोतु',
      selectVatu: 'वाटुवुं चिनोतु',
      search: 'अन्वेषतु',
      loading: 'आरोप्यते...',
      noData: 'दत्तांशः नास्ति',
      previousGrandham: 'पूर्वग्रन्थः',
      currentGrandham: 'वर्तमानग्रन्थः',
      passPercentage: 'उत्तीर्णप्रतिशतम्',
      vatuName: 'वाटुवु नाम',
      pathasalaName: 'पाठशाला नाम',
      reward: 'पुरस्कारः',
      examReward: 'परीक्षा पुरस्कारः',
      expenseAmount: 'व्यय दरः',
      rewardFormula: 'कुलम्',
      close: 'बन्धतु',
      enterAadhar: 'आधार् सङ्ख्यां प्रविशतु',
      aadharRequired: 'आधार् सङ्ख्या आवश्यकी',
      aadharInvalid: 'आधार् १२ अङ्कानां भवितव्यम्',
      results: 'फलानि',
      fullyGiven: 'सर्वथा दत्तानि',
      partiallyGiven: 'आंशिकरूपेण दत्तानि',
      nothingGiven: 'किमपि न दत्तम्',
      parikshaadhikariFeedback: 'परीक्षाधिकारि प्रतिक्रिया',
      positiveFeedback: 'सकारात्मक प्रतिक्रिया',
      improvementFeedback: 'सुधार प्रतिक्रिया',
      parikshaType: 'परीक्षा प्रकारः',
      selectParikshaType: 'परीक्षा प्रकारं चिनोतु',
      krishnaYajurveda: 'कृष्ण यजुर्वेदम्',
      sanskrit: 'संस्कृतम्',
      vedadhyayiSahityam: 'वेदाध्ययि साहित्यम्',
      lakshanam: 'लक्षणम्'
    }
  };

  // Check user roles - all require authentication
  const isAdmin = user && (user.role === 'Admin' || user.role === 'SuperUser');
  const isPathashalaAdmin = user && user.role === 'PathashalaAdmin';
  const isGeneralUser = user && !isAdmin && !isPathashalaAdmin;
  
  // Guests: allow optional Aadhaar search via checkbox (rendering handled in main JSX)

  // Fetch Telugu years
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const res = await api.get('/api/result/years');
        const data = await res.json();
        
        if (res.ok && data.success && data.data) {
          setYearList(data.data);
        }
      } catch (err) {
        console.error('Error fetching years:', err);
      }
    };

    fetchYears();
  }, []);

  // Filter results based on selected pariksha type
  const getFilteredResults = () => {
    if (!selectedParikshaType || parikshaTypes.length <= 1) {
      return allResults;
    }
    return allResults.filter(result => result.pariksha_type === selectedParikshaType);
  };

  // PathashalaAdmin: Fetch vatus for their pathasala first
  useEffect(() => {
    if (!isPathashalaAdmin || !user?.pathasala_id) {
      setVatuList([]);
      return;
    }

    const fetchVatusForPathasala = async () => {
      try {
        const res = await api.get(`/api/vatu/by-pathasala/${user.pathasala_id}`);
        const data = await res.json();
        
        if (res.ok && data.success && Array.isArray(data.data)) {
          setVatuList(data.data);
        }
      } catch (err) {
        console.error('Error fetching vatus:', err);
      }
    };

    fetchVatusForPathasala();
  }, [user, isPathashalaAdmin]);

  // PathashalaAdmin: Fetch all results for selected year and filter by their pathasala's vatus
  useEffect(() => {
    if (!isPathashalaAdmin || !selectedYear || !user || vatuList.length === 0) {
      if (isPathashalaAdmin) {
        setAllResults([]);
      }
      return;
    }

    const fetchAllResults = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/result/by-year?year_id=${encodeURIComponent(selectedYear)}`);
        const data = await res.json();
        
        if (res.ok && data.success && Array.isArray(data.data)) {
          // Create a set of vatu_ids from the pathasala's vatu list
          const pathasalaVatuIds = new Set(vatuList.map(v => v.vatu_id));
          
          // Filter results to only include vatus from the PathashalaAdmin's pathasala
          const filtered = data.data.filter(result => pathasalaVatuIds.has(result.vatu_id));
          
          // Sort by highest score (extract percentage from marks string)
          const sorted = filtered.sort((a, b) => {
            const getScore = (item) => {
              if (!item.grandham_to_show || item.grandham_to_show.length === 0) return 0;
              const currentGrandham = item.grandham_to_show.find(g => !g.isPrevious);
              if (!currentGrandham || !currentGrandham.marks) return 0;
              const match = currentGrandham.marks.match(/(\d+)/);
              return match ? parseInt(match[1]) : 0;
            };
            return getScore(b) - getScore(a);
          });
          
          // Extract unique pariksha types and set default selection
          const uniqueTypes = [...new Set(sorted.map(result => result.pariksha_type).filter(Boolean))];
          setParikshaTypes(uniqueTypes);
          
          // Auto-select Krishna Yajurveda if available, otherwise select first type
          if (uniqueTypes.length === 1) {
            // Only one type, no dropdown needed
            setSelectedParikshaType(uniqueTypes[0]);
          } else if (uniqueTypes.includes('krishna_yajurveda')) {
            setSelectedParikshaType('krishna_yajurveda');
          } else if (uniqueTypes.length > 0) {
            setSelectedParikshaType(uniqueTypes[0]);
          } else {
            setSelectedParikshaType('');
          }
          
          setAllResults(sorted);
        }
      } catch (err) {
        console.error('Error fetching all results:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllResults();
  }, [selectedYear, isPathashalaAdmin, vatuList]);

  // Reset pariksha type when year changes
  useEffect(() => {
    setSelectedParikshaType('');
  }, [selectedYear]);

  // Admin/SuperUser: Fetch all results for selected year
  useEffect(() => {
    if (!isAdmin || !selectedYear) {
      if (isAdmin) {
        setAllResults([]);
      }
      return;
    }

    const fetchAllResults = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/result/by-year?year_id=${encodeURIComponent(selectedYear)}`);
        const data = await res.json();
        
        if (res.ok && data.success && Array.isArray(data.data)) {
          // Sort by highest score (extract percentage from marks string)
          const sorted = data.data.sort((a, b) => {
            const getScore = (item) => {
              if (!item.grandham_to_show || item.grandham_to_show.length === 0) return 0;
              const currentGrandham = item.grandham_to_show.find(g => !g.isPrevious);
              if (!currentGrandham || !currentGrandham.marks) return 0;
              const match = currentGrandham.marks.match(/(\d+)/);
              return match ? parseInt(match[1]) : 0;
            };
            return getScore(b) - getScore(a);
          });
          
          // Extract unique pariksha types and set default selection
          const uniqueTypes = [...new Set(sorted.map(result => result.pariksha_type).filter(Boolean))];
          setParikshaTypes(uniqueTypes);
          
          // Auto-select Krishna Yajurveda if available, otherwise select first type
          if (uniqueTypes.length === 1) {
            // Only one type, no dropdown needed
            setSelectedParikshaType(uniqueTypes[0]);
          } else if (uniqueTypes.includes('krishna_yajurveda')) {
            setSelectedParikshaType('krishna_yajurveda');
          } else if (uniqueTypes.length > 0) {
            setSelectedParikshaType(uniqueTypes[0]);
          } else {
            setSelectedParikshaType('');
          }
          
          setAllResults(sorted);
        }
      } catch (err) {
        console.error('Error fetching all results:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllResults();
  }, [selectedYear, isAdmin]);

  // General User: Search by aadhar
  const handleAadharSearch = async () => {
    setAadharError('');
    setSearchError('');
    
    if (!aadhar) {
      setAadharError(labels[lang].aadharRequired);
      return;
    }
    
    if (!/^\d{12}$/.test(aadhar)) {
      setAadharError(labels[lang].aadharInvalid);
      return;
    }
    
    if (!selectedYear) {
      setSearchError(labels[lang].selectYear);
      return;
    }

    setLoading(true);
    try {
      const res = await api.get(
        `/api/result/by-year-aadhar?year_id=${encodeURIComponent(selectedYear)}&aadhar=${encodeURIComponent(aadhar)}`
      );
      const data = await res.json();

      if (res.ok && data.success && data.data) {
        const savedResult = data.data;
        const grandhamData = savedResult.grandham_to_show || [];
        
        const prevGrandham = grandhamData.filter(item => item.isPrevious === true);
        const currGrandham = grandhamData.filter(item => item.isPrevious !== true);
        
        setGrandhamToShow({ previous: prevGrandham, current: currGrandham });
        
        const marksData = {};
        grandhamData.forEach((item, idx) => {
          marksData[idx] = item.marks || '';
        });
        setMarks(marksData);
        
        const ratingsData = {};
        grandhamData.forEach((item, idx) => {
          const key = `${item.isPrevious ? 'prev' : 'curr'}_${item.section}_${idx}`;
          ratingsData[key] = item.rating || {};
        });
        setRatings(ratingsData);
        
        // Store feedback for display
        setSelectedResult(savedResult);
      } else {
        setSearchError(data.error || labels[lang].noData);
      }
    } catch (err) {
      console.error('Error searching by aadhar:', err);
      setSearchError(labels[lang].noData);
    } finally {
      setLoading(false);
    }
  };

  // Open dialog with result details
  const handleRowClick = async (result) => {
    setSelectedResult(result);
    
    const grandhamData = result.grandham_to_show || [];
    const prevGrandham = grandhamData.filter(item => item.isPrevious === true);
    const currGrandham = grandhamData.filter(item => item.isPrevious !== true);
    
    setGrandhamToShow({ previous: prevGrandham, current: currGrandham });
    
    const marksData = {};
    grandhamData.forEach((item, idx) => {
      marksData[idx] = item.marks || '';
    });
    setMarks(marksData);
    
    // Extract ratings from pariksha_result array (new format for previous grandham)
    const extractRatingsFromParikshaResult = (parikshaResults) => {
      const ratingsMap = {};
      if (Array.isArray(parikshaResults)) {
        parikshaResults.forEach(item => {
          if (item.section && item.rating && typeof item.rating === 'object') {
            if (!ratingsMap[item.section]) ratingsMap[item.section] = {};
            Object.keys(item.rating).forEach(kanda => {
              if (!ratingsMap[item.section][kanda]) ratingsMap[item.section][kanda] = {};
              Object.keys(item.rating[kanda]).forEach(pannam => {
                ratingsMap[item.section][kanda][pannam] = item.rating[kanda][pannam];
              });
            });
          }
        });
      }
      return ratingsMap;
    };
    
    const parikshaResultRatings = extractRatingsFromParikshaResult(result.pariksha_result);
    
    const ratingsData = {};
    
    // Build ratings for previous grandhams (using filtered indices)
    prevGrandham.forEach((item, idx) => {
      const key = `prev_${item.section}_${idx}`;
      // Use ratings from pariksha_result (new format) if available, otherwise use grandham_to_show (old format)
      if (parikshaResultRatings[item.section]) {
        ratingsData[key] = parikshaResultRatings[item.section];
      } else {
        ratingsData[key] = item.rating || {};
      }
    });
    
    // Build ratings for current grandhams (using filtered indices)
    currGrandham.forEach((item, idx) => {
      const key = `curr_${item.section}_${idx}`;
      // Use rating from grandham_to_show if available
      ratingsData[key] = item.rating || {};
    });
    
    setRatings(ratingsData);
    setDialogOpen(true);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Breadcrumb items={[
        { label: lang === 'telugu' ? 'పరీక్ష' : 'परीक्षा', path: '/parikshaMgmt' },
        { label: lang === 'telugu' ? 'ఫలితాలు' : 'फलानि', path: '/results' }
      ]} />
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2, backgroundColor: '#fff' }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            mb: { xs: 3, md: 4 },
            color: '#7f4b2d',
            fontWeight: 600,
            textAlign: 'center',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2rem' },
            borderBottom: '3px solid #7f4b2d',
            pb: 2
          }}
        >
          {labels[lang].title}
        </Typography>



        {/* Year Selection (Common for all roles) */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>{labels[lang].teluguYear}</InputLabel>
            <Select
              value={selectedYear}
              label={labels[lang].teluguYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <MenuItem value="">
                <em>{labels[lang].selectYear}</em>
              </MenuItem>
              {yearList.map((year) => (
                <MenuItem key={year.value} value={year.value}>
                  {year.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Pariksha Type Selection Dropdown (shown only if multiple types exist) */}
        {(isPathashalaAdmin || isAdmin) && parikshaTypes.length > 1 && selectedYear && (
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>{labels[lang].parikshaType}</InputLabel>
              <Select
                value={selectedParikshaType}
                label={labels[lang].parikshaType}
                onChange={(e) => setSelectedParikshaType(e.target.value)}
              >
                <MenuItem value="">
                  <em>{labels[lang].selectParikshaType}</em>
                </MenuItem>
                {parikshaTypes.map((type) => {
                  const typeLabels = {
                    'krishna_yajurveda': labels[lang].krishnaYajurveda,
                    'sanskrit': labels[lang].sanskrit,
                    'vedadhyayi_sahityam': labels[lang].vedadhyayiSahityam,
                    'lakshanam': labels[lang].lakshanam
                  };
                  return (
                    <MenuItem key={type} value={type}>
                      {typeLabels[type] || type}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* PathashalaAdmin View - Table View (filtered to their pathasala only) */}
        {isPathashalaAdmin && (
          <>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!loading && selectedYear && allResults.length > 0 && (
              <TableContainer component={Paper} sx={{ mt: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#7f4b2d' }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{labels[lang].vatuName}</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{labels[lang].passPercentage}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredResults().map((result, index) => {
                      const currentGrandham = result.grandham_to_show?.find(g => !g.isPrevious);
                      const percentage = currentGrandham?.marks || '-';
                      
                      return (
                        <TableRow
                          key={result.pariksha_result_id}
                          onClick={() => handleRowClick(result)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                          }}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell sx={{ color: '#1976d2', fontWeight: 500 }}>
                            {result.vatu_name}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{percentage}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!loading && selectedYear && allResults.length === 0 && (
              <Typography sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                {labels[lang].noData}
              </Typography>
            )}
          </>
        )}

        {/* Admin/SuperUser View */}
        {isAdmin && (
          <>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!loading && selectedYear && allResults.length > 0 && (
              <TableContainer component={Paper} sx={{ mt: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#7f4b2d' }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{labels[lang].vatuName}</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{labels[lang].pathasalaName}</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{labels[lang].passPercentage}</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{labels[lang].examReward + ' + ' + labels[lang].expenseAmount + ' = ' + labels[lang].rewardFormula}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredResults().map((result, index) => {
                      const currentGrandham = result.grandham_to_show?.find(g => !g.isPrevious);
                      const percentage = currentGrandham?.marks || '-';
                      
                      return (
                        <TableRow
                          key={result.pariksha_result_id}
                          onClick={() => handleRowClick(result)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                          }}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell sx={{ color: '#1976d2', fontWeight: 500 }}>
                            {result.vatu_name}
                          </TableCell>
                          <TableCell>{result.pathasala_name}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{percentage}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {result.examReward !== undefined || result.expenseAmount !== undefined ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.85rem' }}>
                                  {`${result.examReward || 0} + ${result.expenseAmount || 0} = ${result.reward || '-'}`}
                                </Typography>
                                {/* <Typography variant="body2" sx={{ fontWeight: 700, color: '#1976d2' }}>
                                  {`= ${result.reward || '-'}`}
                                </Typography> */}
                              </Box>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!loading && selectedYear && allResults.length === 0 && (
              <Typography sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                {labels[lang].noData}
              </Typography>
            )}
          </>
        )}

        {/* General User View (also available to guests) */}
        {(isGeneralUser || !user) && (
          <>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label={labels[lang].aadhar}
                value={aadhar}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                  setAadhar(value);
                  setAadharError('');
                }}
                error={!!aadharError}
                helperText={aadharError}
                placeholder={labels[lang].enterAadhar}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAadharSearch}
                disabled={loading || !selectedYear}
                sx={{ py: 1.5, fontSize: '1rem' }}
              >
                {loading ? <CircularProgress size={24} /> : labels[lang].search}
              </Button>
            </Box>

            {searchError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {searchError}
              </Alert>
            )}

            {!loading && grandhamToShow.current.length > 0 && (
              <ResultDisplay
                grandhamToShow={grandhamToShow}
                marks={marks}
                ratings={ratings}
                lang={lang}
                labels={labels[lang]}
                parikshaadhikariFeedback={selectedResult?.parikshaadhikari_feedback}
              />
            )}
          </>
        )}
      </Paper>

      {/* Dialog for Admin/SuperUser to view detailed results */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{selectedResult?.vatu_name} - {labels[lang].results}</Typography>
          <IconButton onClick={() => setDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <ResultDisplay
            grandhamToShow={grandhamToShow}
            marks={marks}
            ratings={ratings}
            lang={lang}
            labels={labels[lang]}
            parikshaadhikariFeedback={selectedResult?.parikshaadhikari_feedback}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {labels[lang].close}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
